/**
 * The client's one audio engine + director (AD-861), and the browser wiring:
 * first-gesture unlock, page visibility, UI tap/open/close, the feedback-bus subscription and the
 * Settings store. `installAudio()` is called once from main.tsx.
 */
import cueData from './cues.generated.json' with { type: 'json' };
import voiceData from './voice.generated.json' with { type: 'json' };
import { gameFeedback } from '../feedback/feedbackBus.js';
import { activeQualityProfile } from '../scene/quality.js';
import type { CueMap } from './cueMap.js';
import { preloadFor } from './cueMap.js';
import { AudioDirector } from './director.js';
import type { AudioPorts, ContextLike, MediaLike } from './engine.js';
import { AudioEngine } from './engine.js';
import type { AudioBus, AudioSettings } from './settings.js';
import type { TalkLike, VoiceChoice, VoiceMap } from './voice.js';
import { ConversationVoice, VoiceSequencer } from './voice.js';
import { loadAudioSettings, saveAudioSettings } from './settings.js';

const voiceMap = (voiceData as unknown as { voice: VoiceMap }).voice;
// Spoken lines are voice-bus cues generated from content/audio/voice-lines.json, merged over the SFX map.
const map = {
  ...(cueData as unknown as CueMap),
  sfx: {
    ...(cueData as unknown as CueMap).sfx,
    ...(voiceData as unknown as { sfx: CueMap['sfx'] }).sfx,
  },
} as CueMap;

const storage = (): Storage | null => {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
};

const baseUrl = (): string => {
  const base = (import.meta as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? '/';
  return `${base.replace(/\/+$/, '')}/audio`;
};

/** Browser ports: the real AudioContext, fetch, <audio>. */
export function browserAudioPorts(): AudioPorts {
  return {
    createContext() {
      const w = globalThis as unknown as {
        AudioContext?: new (o?: { latencyHint?: string }) => ContextLike;
        webkitAudioContext?: new () => ContextLike;
        navigator?: { audioSession?: { type: string } };
      };
      // iOS 16.4+: "ambient" mixes with the player's own audio and obeys the ringer switch.
      try {
        if (w.navigator?.audioSession) w.navigator.audioSession.type = 'ambient';
      } catch {
        /* not supported */
      }
      const Ctor = w.AudioContext ?? w.webkitAudioContext;
      if (!Ctor) return null;
      try {
        return new Ctor({ latencyHint: 'interactive' });
      } catch {
        return null;
      }
    },
    async fetchBytes(url) {
      const ctl = new AbortController();
      const timer = setTimeout(() => ctl.abort(), 15_000);
      try {
        const res = await fetch(url, { signal: ctl.signal });
        return res.ok ? await res.arrayBuffer() : null;
      } catch {
        return null;
      } finally {
        clearTimeout(timer);
      }
    },
    createMedia() {
      if (typeof Audio === 'undefined') return null;
      const el = new Audio();
      el.crossOrigin = 'anonymous';
      return el as unknown as MediaLike;
    },
    canPlayOgg() {
      if (typeof Audio === 'undefined') return false;
      return new Audio().canPlayType('audio/ogg; codecs=opus') !== '';
    },
    now: () => performance.now(),
  };
}

let settings: AudioSettings = loadAudioSettings(storage());
const listeners = new Set<(s: AudioSettings) => void>();

const low = (): boolean => {
  try {
    return activeQualityProfile().low;
  } catch {
    return false;
  }
};

export const audioEngine = new AudioEngine(map, browserAudioPorts(), settings, {
  baseUrl: baseUrl(),
});

export const audioDirector = new AudioDirector(map, audioEngine, () => performance.now());

const barkListeners = new Set<(speaker: string, text: string) => void>();
audioDirector.setVoice(voiceMap, (who, text) => {
  for (const l of barkListeners) l(who, text);
});

/** A bark was spoken: show `who: text` as its subtitle. */
export function subscribeBarks(fn: (speaker: string, text: string) => void): () => void {
  barkListeners.add(fn);
  return () => barkListeners.delete(fn);
}

const sequencer = new VoiceSequencer(audioEngine);

const conversations = new ConversationVoice(voiceMap, sequencer);

/**
 * A conversation opened (`seq` = the talk's sequence number): speak its greeting, once a session
 * (AD-870; q-npc-lore-talk-option). Nothing else speaks until the player chooses it. The subtitles
 * are the dialogue box's own text. Re-showing the same `seq` is a no-op.
 */
export function voiceTalk(seq: number, d: TalkLike): void {
  conversations.open(seq, d);
}

/**
 * The player made a choice in a conversation (asked about a quest, accepted, declined, asked how
 * it goes, turned it in, asked a "tell me about…" topic, said goodbye): speak its answer now
 * (AD-870). Subtitle: the panel's text.
 */
export function voiceChoice(d: TalkLike, choice: VoiceChoice): void {
  conversations.choose(d, choice);
}

/** Dialogue closed or the next talk began: cut the line and the rest. */
export function voiceStop(): void {
  conversations.stop();
}

let zone: string | null = null;

/** Game.onReady: a new zone — preload its cues and reset the music to the yard. */
export function audioEnterZone(zoneId: string): void {
  zone = zoneId;
  audioDirector.enterZone();
  void audioEngine.preload(preloadFor(map, zoneId));
}

export function getAudioSettings(): AudioSettings {
  return settings;
}

export function setBusSetting(bus: AudioBus, patch: Partial<AudioSettings[AudioBus]>): void {
  settings = { ...settings, [bus]: { ...settings[bus], ...patch } };
  saveAudioSettings(storage(), settings);
  audioEngine.applySettings(settings);
  for (const l of listeners) l(settings);
}

export function subscribeAudioSettings(fn: (s: AudioSettings) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** UI cues not on the feedback bus (tap / window open / close / quest accept). */
export function playUiCue(cue: 'ui.tap' | 'ui.open' | 'ui.close' | 'quest.accept'): void {
  audioEngine.play(cue);
}

let installed = false;

export function installAudio(doc: Document = document): void {
  if (installed) return;
  installed = true;
  const gestures = ['pointerdown', 'touchend', 'keydown', 'click'] as const;
  const onGesture = (): void => {
    audioEngine.configureTier(low());
    void audioEngine.unlock().then((ok) => {
      if (!ok) return;
      for (const g of gestures) doc.removeEventListener(g, onGesture, true);
      audioEngine.resumeMusic();
      void audioEngine.preload(preloadFor(map, zone));
    });
  };
  for (const g of gestures) doc.addEventListener(g, onGesture, { capture: true, passive: true });
  doc.addEventListener('visibilitychange', () => {
    void audioEngine.setHidden(doc.visibilityState === 'hidden');
  });
  // UI tap: any button press (delegated; the panels need no per-button wiring).
  doc.addEventListener(
    'click',
    (e) => {
      const t = e.target as Element | null;
      if (t && typeof t.closest === 'function' && t.closest('button')) playUiCue('ui.tap');
    },
    { capture: true, passive: true },
  );
  gameFeedback.subscribe((ev) => audioDirector.onFeedback(ev));
  setInterval(() => audioDirector.tick(), 1000);
  // Lane/DEV evidence: the cue log is readable from the page (audio cannot be filmed).
  (globalThis as { __gcAudio?: unknown }).__gcAudio = {
    log: () => audioEngine.cueLog(),
    state: () => ({
      unlocked: audioEngine.unlocked,
      music: audioEngine.currentMusicState,
      decodedBytes: audioEngine.decodedBytesHeld,
      budget: audioEngine.options.decodedBudgetBytes,
    }),
  };
}
